import { cx } from '@linaria/core';
import { useEffect, useRef, useMemo } from 'react';
import { assertRefObject, useKatex, NoSSR, SizeTransition } from '@utils/react';
import { usePageContext } from '../../context.js';
import { useOpgavekompleksetComponent } from '../../state/context.data.js';
import { useOpenTaskMutation } from '../../state/mutations/task.open.js';
import { getAudienceForTask } from '../../utils/get-audience-for-task.js';
import { taskSelectors } from './_task.selectors.js';
import { introductionTransitionMs, introductionWrapper, contentContainer, content, wrapper } from './_task.style.js';
import { useTaskContext, TaskMeta } from './context.js';
import { useIntroductionReady } from './hook.introduction-ready.js';
import { useMatchTaskBreakpoint, taskBreakpointsCls, taskBreakpoints } from './hook.task-breakpoints.js';
import { Hints } from './molecule.hints/_hints.js';
import { TaskIntroduction } from './molecule.introduction/_introduction.js';
import { FreeTextTask } from './molecule.task-content/freetext/_freetext.js';
import { GroupTask } from './molecule.task-content/group/_task.group.js';
import { InfoPageTask } from './molecule.task-content/infopage/_infopage.js';
import { MatchTask } from './molecule.task-content/match/_task.match.js';
import { PlaceItemsInTextTask } from './molecule.task-content/place-items.in-text/_task.in-text.js';
import { PlaceItemsOnImageTask } from './molecule.task-content/place-items.on-image/_task.on-image.js';
import { PlaceTransparentImagesOnImageTask } from './molecule.task-content/place-items.transparent-images-on-image/_task.transparent-images-on-image.js';
import { SelectAnswersCheckboxTask } from './molecule.task-content/select-answers.checkbox/_task.checkbox.js';
import { SelectAnswersDropdownTask } from './molecule.task-content/select-answers.dropdown/_task.dropdown.js';
import { SelectAnswersHyphenateWords } from './molecule.task-content/select-answers.hyphenate-words/_task.hyphenate-words.js';
import { SelectAnswersMarkCharacters } from './molecule.task-content/select-answers.mark-characters/_task.mark-characters.js';
import { SelectAnswersMarkWords } from './molecule.task-content/select-answers.mark-words/_task.mark-words.js';
import { SelectAnswersPoolCouponTask } from './molecule.task-content/select-answers.pool-coupon/_task.pool-coupon.js';
import { SelectAnswersRadioButtonTask } from './molecule.task-content/select-answers.radio-button/_task.radio-button.js';
import { SequenceSentenceTask } from './molecule.task-content/sequence.sentence/_task.sentence.js';
import { SequenceSequenceTask } from './molecule.task-content/sequence.sequence/_task.sentence.js';
import { TextCorrectionTask } from './molecule.task-content/text-correction/_text-correction.js';
import { WriteAnswersInTableTask } from './molecule.task-content/write-answers.in-table/_task.in-table.js';
import { WriteAnswersInTextTask } from './molecule.task-content/write-answers.in-text/_task.in-text.js';
import { WriteAnswersOnImageTask } from './molecule.task-content/write-answers.on-image/_task.on-image.js';
import { TaskHeader } from './molecule.task-header/_task-header.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function Task(props) {
  const onOpened = useOpenTaskMutation();
  const context = useTaskContext();
  useEffect(() => {
    if (context === "input") {
      onOpened(props.task.id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task.id]);
  if (props.task.type === "stack") {
    return /*#__PURE__*/jsx("div", {
      ref: props.ref,
      className: cx(props.className, taskSelectors.root.className),
      children: props.task.tasks.map((task, index) => /*#__PURE__*/jsx(SingleTask, {
        task: task,
        stackId: props.task.id,
        taskIndex: props.taskIndex,
        indexInStack: index
      }, task.id))
    });
  } else {
    return /*#__PURE__*/jsx(SingleTask, {
      ...props,
      ref: props.ref,
      task: props.task
    });
  }
}
const SingleTask = function SingleTask(props) {
  const internalRef = useRef(null);
  const ref = props.ref ?? internalRef;
  assertRefObject(ref);
  const isTiny = useMatchTaskBreakpoint(taskBreakpoints.tiny);
  const isMobile = useMatchTaskBreakpoint(taskBreakpoints.mobile);
  const isTablet = useMatchTaskBreakpoint(taskBreakpoints.tablet);
  const isDesktop = useMatchTaskBreakpoint(taskBreakpoints.desktop);
  const component = useOpgavekompleksetComponent();
  const pageContext = usePageContext();
  const context = useTaskContext();
  const isIntroductionReady = useIntroductionReady(props.task.introduction);
  useKatex(ref);
  const taskEntryKey = props.stackId ?? props.task.id;
  const content$1 = (() => {
    switch (props.task.taskType) {
      case "WriteAnswers":
        {
          switch (props.task.answerType) {
            case "InText":
              return /*#__PURE__*/jsx(WriteAnswersInTextTask, {
                taskEntryKey: taskEntryKey,
                task: props.task
              });
            case "InTable":
              return /*#__PURE__*/jsx(WriteAnswersInTableTask, {
                taskEntryKey: taskEntryKey,
                task: props.task
              });
            case "OnImage":
              return /*#__PURE__*/jsx(WriteAnswersOnImageTask, {
                taskEntryKey: taskEntryKey,
                task: props.task
              });
          }
        }

      // eslint-disable-next-line no-fallthrough
      case "SelectAnswers":
        switch (props.task.answerType) {
          case "DropDown":
            return /*#__PURE__*/jsx(SelectAnswersDropdownTask, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
          case "Checkbox":
            return /*#__PURE__*/jsx(SelectAnswersCheckboxTask, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
          case "RadioButton":
            return /*#__PURE__*/jsx(SelectAnswersRadioButtonTask, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
          case "MarkWords":
            return /*#__PURE__*/jsx(SelectAnswersMarkWords, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
          case "MarkCharacters":
            return /*#__PURE__*/jsx(SelectAnswersMarkCharacters, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
          case "HyphenateWords":
            return /*#__PURE__*/jsx(SelectAnswersHyphenateWords, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
          case "PoolCoupon":
            return /*#__PURE__*/jsx(SelectAnswersPoolCouponTask, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
        }

      // eslint-disable-next-line no-fallthrough
      case "PlaceItems":
        switch (props.task.answerType) {
          case "InText":
            return /*#__PURE__*/jsx(PlaceItemsInTextTask, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
          case "SolidItemsOnImage":
            return /*#__PURE__*/jsx(PlaceItemsOnImageTask, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
          case "TransparentImagesOnImage":
            return /*#__PURE__*/jsx(PlaceTransparentImagesOnImageTask, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
        }

      // eslint-disable-next-line no-fallthrough
      case "Sequence":
        switch (props.task.answerType) {
          case "Sentence":
            return /*#__PURE__*/jsx(SequenceSentenceTask, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
          case "Vertical":
          case "Horizontal":
          case "Circuit":
            return /*#__PURE__*/jsx(SequenceSequenceTask, {
              taskEntryKey: taskEntryKey,
              task: props.task
            });
        }

      // eslint-disable-next-line no-fallthrough
      case "Match":
        return /*#__PURE__*/jsx(MatchTask, {
          taskEntryKey: taskEntryKey,
          task: props.task
        });
      case "Group":
        return /*#__PURE__*/jsx(GroupTask, {
          taskEntryKey: taskEntryKey,
          task: props.task
        });
      case "FreeText":
        return /*#__PURE__*/jsx(FreeTextTask, {
          taskEntryKey: taskEntryKey,
          task: props.task
        });
      case "InfoPage":
        return /*#__PURE__*/jsx(InfoPageTask, {
          task: props.task
        });
      case "TextCorrection":
        return /*#__PURE__*/jsx(TextCorrectionTask, {
          taskEntryKey: taskEntryKey,
          task: props.task
        });
      default:
        console.warn("unrecognized task", props.task);
        return null;
    }
  })();
  const audience = getAudienceForTask(props.task, component.data.layout?.type);

  // NB: on the input-page, then the first task header will be handled
  // externally as it also contains other tools such as fullscreen, etc.
  const showHeaderForTask = !!props.stackId && (pageContext !== "input" || props.indexInStack !== 0);
  const meta = useMemo(() => ({
    taskId: props.task.id,
    taskEntryKey
  }), [props.task.id, taskEntryKey]);
  const showTaskInSplitView = (() => {
    const isInSplitviewBreakpoint = !isTiny && !isMobile && !isTablet && pageContext !== "print";
    if (!isInSplitviewBreakpoint) {
      return false;
    }

    // For Checkbox and RadioButton specifically we implement a split-view
    // with the introduction.
    // See ADO #125353
    switch (props.task.taskType) {
      // @ts-expect-error We explictly want to fallthrough to our
      // default case if nothing matches fully.
      case "WriteAnswers":
        {
          switch (props.task.answerType) {
            case "InText":
              return true;
          }
        }

      // @ts-expect-error We explictly want to fallthrough to our
      // default case if nothing matches fully.
      // eslint-disable-next-line no-fallthrough
      case "SelectAnswers":
        {
          switch (props.task.answerType) {
            case "DropDown":
              return true;
          }
        }

      // eslint-disable-next-line no-fallthrough
      default:
        return false;
    }
  })();
  const withIntroduction = !!props.task.introduction.length && !showTaskInSplitView;
  return /*#__PURE__*/jsx(TaskMeta.Provider, {
    value: meta,
    children: /*#__PURE__*/jsxs("div", {
      ref: ref,
      className: cx(props.className, wrapper, taskBreakpointsCls, !props.stackId && taskSelectors.root.className),
      "data-tiny": isTiny,
      "data-mobile": isMobile,
      "data-tablet": isTablet,
      "data-desktop": isDesktop,
      children: [showHeaderForTask && /*#__PURE__*/jsx(TaskHeader, {
        ...props,
        type: "stack",
        task: props.task,
        taskIndex: props.taskIndex,
        withProgress: false
      }), /*#__PURE__*/jsx(NoSSR, {
        children: /*#__PURE__*/jsx(SizeTransition, {
          in: isIntroductionReady && withIntroduction,
          axis: "vertical",
          classNames: introductionWrapper,
          timeout: introductionTransitionMs,
          delayMs: {
            enter: 10,
            exit: 10
          },
          mountOnEnter: true,
          unmountOnExit: true,
          children: ref => /*#__PURE__*/jsx("div", {
            ref: ref,
            className: introductionWrapper,
            "data-page-context": pageContext,
            children: /*#__PURE__*/jsx(TaskIntroduction, {
              introduction: props.task.introduction,
              indexInStack: props.indexInStack
            })
          })
        })
      }), /*#__PURE__*/jsxs("div", {
        className: cx(content, taskSelectors.content.className),
        "data-reapply-font-scales": true,
        "data-page-context": pageContext,
        "data-in-stack": !!props.stackId,
        "data-has-header": showHeaderForTask || pageContext === "input",
        "data-has-introduction": withIntroduction,
        "data-audience": audience,
        children: [!showTaskInSplitView && context === "input" && /*#__PURE__*/jsx(Hints, {
          taskEntryKey: props.stackId ?? props.task.id,
          task: props.task
        }), /*#__PURE__*/jsx("div", {
          className: contentContainer,
          children: content$1
        })]
      })]
    })
  });
};

export { Task };
//# sourceMappingURL=_task.js.map
